import { api } from '@/shared/api/client';

import type { Email, EmailDetail, TriageCorrection, TriageDecision } from './types';

/**
 * The two calls about one email that more than one feature needs.
 *
 * The inbox owns the email screens (`features/emails`); the quote editor needs to read
 * one email and requeue it while it waits for an uploaded document to be read (contract
 * v8). A feature never imports another feature, so what both need lives here, and the
 * inbox's `emailKeys.detail` **is** `emailDetailKey`: one key, so an invalidation from
 * either side reaches the other's cache entry.
 */
/** Every email query starts here: the inbox lists, the counts, the detail. Invalidating
 * it reloads all of them (a renamed mailbox, contract v10.23). */
export const emailsRootKey = ['emails'] as const;
export const emailDetailKey = (id: string) => [...emailsRootKey, 'detail', id] as const;
/** Every inbox list, whatever its filters: the inbox's `emailKeys.lists` is this. */
export const emailListsKey = [...emailsRootKey, 'list'] as const;
/** The counts of the inbox rail (contract v10.25), whatever the mailbox. */
export const emailViewsKey = [...emailsRootKey, 'views'] as const;
/** Where each email sits in its list (contract v10.25): «2 de 5», «Siguiente». */
export const emailNeighboursKey = [...emailsRootKey, 'neighbours'] as const;

export const sharedEmailsApi = {
  detail: (id: string): Promise<EmailDetail> => api.get<EmailDetail>(`/api/emails/${id}`),

  /**
   * Back to `pending` and into the queue again. The back keeps the decision it already
   * has and retries only what is missing: for an uploaded document, the reading.
   */
  reprocess: (id: string): Promise<Email> => api.post<Email>(`/api/emails/${id}/reprocess`),

  /**
   * Copy a message the connector read from the spam folder into the inbox (contract
   * v10.4). The worker does it over IMAP; the original stays in spam. The only action a
   * spam message admits, and always a person's.
   */
  rescue: (id: string): Promise<EmailDetail> => api.post<EmailDetail>(`/api/emails/${id}/rescue`),

  /**
   * What the email is and where it goes, according to a person: «Correcto» sends the
   * proposal back, «Corregir…» a different destination, category or both (contract
   * v10.24). Here and not in the inbox since v10.24, because the quote editor corrects too
   * («Esto no es una oferta de proveedor»).
   */
  correctTriage: (id: string, correction: TriageCorrection): Promise<TriageDecision> =>
    api.post<TriageDecision>(`/api/emails/${id}/triage/correct`, correction),
};
